/*
 * K-Town Bulgogi — Google Maps Store Locator
 * ------------------------------------------------------------------
 * Progressive enhancement for the store-locator section. The store
 * list is rendered server-side in Liquid (works with no JS); this
 * script layers the interactive Google Map on top and wires the
 * list <-> map <-> search together.
 *
 * Logic is split into small, single-responsibility modules:
 *   - GoogleMapsLoader : loads the Maps JS API once, on demand.
 *   - StoreMap         : owns the map, markers and info window.
 *   - StoreList        : owns the server-rendered list DOM.
 *   - StoreSearch      : owns the search input + filtering.
 *   - StoreLocator     : orchestrates the three above.
 *
 * No globals are leaked except a single namespaced Maps callback.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------
   * GoogleMapsLoader — load the Maps JS API exactly once.
   * ------------------------------------------------------------- */
  var GoogleMapsLoader = (function () {
    var promise = null;

    function load(apiKey) {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }
      if (promise) {
        return promise;
      }
      promise = new Promise(function (resolve, reject) {
        var callbackName = '__ktownGmapsReady';
        window[callbackName] = function () {
          resolve();
          try {
            delete window[callbackName];
          } catch (e) {
            window[callbackName] = undefined;
          }
        };
        var script = document.createElement('script');
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=' +
          encodeURIComponent(apiKey) +
          '&callback=' +
          callbackName +
          '&loading=async&v=weekly';
        script.async = true;
        script.onerror = function () {
          reject(new Error('Google Maps failed to load'));
        };
        document.head.appendChild(script);
      });
      return promise;
    }

    return { load: load };
  })();

  /* ---------------------------------------------------------------
   * Small SVG marker icons (brand-styled comic pins).
   * ------------------------------------------------------------- */
  function pinSvg(fill) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">' +
      '<path d="M20 1C10 1 2 9 2 19c0 12 15 28 17 31 .5.8 1.5.8 2 0 2-3 17-19 17-31C38 9 30 1 20 1Z" ' +
      'fill="' + fill + '" stroke="#1a1512" stroke-width="2.5"/>' +
      '<circle cx="20" cy="19" r="7" fill="#fffaf3" stroke="#1a1512" stroke-width="2.5"/>' +
      '</svg>'
    );
  }

  function markerIcon(google, fill, size) {
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(pinSvg(fill)),
      scaledSize: new google.maps.Size(size, size * 1.3),
      anchor: new google.maps.Point(size / 2, size * 1.3),
    };
  }

  /* A distinct "your search location" pin, so it never reads as a store. */
  function searchPinSvg() {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">' +
      '<circle cx="15" cy="15" r="13" fill="#1a1512" opacity="0.14"/>' +
      '<circle cx="15" cy="15" r="7.5" fill="#007bc1" stroke="#fffaf3" stroke-width="3"/>' +
      '</svg>'
    );
  }

  function searchIcon(google) {
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(searchPinSvg()),
      scaledSize: new google.maps.Size(30, 30),
      anchor: new google.maps.Point(15, 15),
    };
  }

  /* Great-circle distance between two lat/lng points, in miles. */
  function haversineMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.8; // Earth radius, miles
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) *
        Math.cos(lat2 * toRad) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* Human-friendly miles label ("4.2 mi" close in, "37 mi" farther out). */
  function formatMiles(miles) {
    return (miles < 10 ? miles.toFixed(1) : Math.round(miles)) + ' mi';
  }

  /* ---------------------------------------------------------------
   * StoreMap — the Google Map, its markers and the info window.
   * ------------------------------------------------------------- */
  function StoreMap(canvas, stores, options) {
    this.canvas = canvas;
    this.stores = stores;
    this.options = options || {};
    this.google = window.google;
    this.markers = [];
    this.onMarkerSelect = function () {};

    var google = this.google;
    this.colors = {
      base: options.pinColor || '#ed1e24',
      active: options.pinActiveColor || '#007bc1',
    };

    this.map = new google.maps.Map(canvas, {
      zoom: 5,
      center: { lat: 39.5, lng: -98.35 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
      gestureHandling: 'cooperative',
      styles: this.options.mapStyles || null,
    });

    this.infoWindow = new google.maps.InfoWindow({ maxWidth: 440 });
    this._buildMarkers();
  }

  StoreMap.prototype._buildMarkers = function () {
    var self = this;
    var google = this.google;

    this.stores.forEach(function (store, index) {
      if (isNaN(store.lat) || isNaN(store.lng)) {
        return;
      }
      var marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map: self.map,
        title: store.name,
        icon: markerIcon(google, self.colors.base, 40),
        optimized: false,
      });
      marker.storeIndex = index;
      marker.addListener('click', function () {
        self.onMarkerSelect(index);
      });
      self.markers[index] = marker;
    });
  };

  /**
   * Fit the viewport to every currently visible marker. Pass an optional
   * extra lat/lng (the search location) to keep it in frame too.
   */
  StoreMap.prototype.fitTo = function (indices, extraLatLng) {
    var google = this.google;
    var points = [];
    this.markers.forEach(function (m, i) {
      if (m && indices.indexOf(i) !== -1) {
        points.push(m.getPosition());
      }
    });
    if (extraLatLng) {
      points.push(extraLatLng);
    }
    if (!points.length) {
      return;
    }
    if (points.length === 1) {
      this.map.setCenter(points[0]);
      this.map.setZoom(13);
      return;
    }
    var bounds = new google.maps.LatLngBounds();
    points.forEach(function (p) {
      bounds.extend(p);
    });
    this.map.fitBounds(bounds, 64);
  };

  /** Drop (or move) the marker showing the searched-for location. */
  StoreMap.prototype.setSearchMarker = function (latLng) {
    var google = this.google;
    if (!this.searchMarker) {
      this.searchMarker = new google.maps.Marker({
        map: this.map,
        icon: searchIcon(google),
        zIndex: 500,
        clickable: false,
        title: 'Your search location',
      });
    }
    this.searchMarker.setPosition(latLng);
    this.searchMarker.setMap(this.map);
  };

  /** Remove the search-location marker (e.g. when the query is cleared). */
  StoreMap.prototype.clearSearchMarker = function () {
    if (this.searchMarker) {
      this.searchMarker.setMap(null);
    }
  };

  /** Toggle marker visibility to match the filtered store list. */
  StoreMap.prototype.setVisible = function (indices) {
    var self = this;
    this.markers.forEach(function (m, i) {
      if (!m) {
        return;
      }
      m.setMap(indices.indexOf(i) !== -1 ? self.map : null);
    });
  };

  /** Smoothly pan + zoom to a store and open its info window. */
  StoreMap.prototype.focus = function (index) {
    var marker = this.markers[index];
    if (!marker) {
      return;
    }
    var self = this;
    this._highlight(index);
    this.map.panTo(marker.getPosition());
    this._smoothZoom(15, this.map.getZoom());
    this.infoWindow.setContent(this._infoContent(this.stores[index]));
    this.infoWindow.open({ map: this.map, anchor: marker });
    // Nudge the marker so the selection reads on the map too.
    marker.setAnimation(this.google.maps.Animation.BOUNCE);
    window.setTimeout(function () {
      marker.setAnimation(null);
    }, 700);
    google.maps.event.addListenerOnce(this.infoWindow, 'closeclick', function () {
      self._highlight(-1);
    });
  };

  /** Recolour markers so the active one stands out. */
  StoreMap.prototype._highlight = function (activeIndex) {
    var self = this;
    var google = this.google;
    this.markers.forEach(function (m, i) {
      if (!m) {
        return;
      }
      var isActive = i === activeIndex;
      m.setIcon(markerIcon(google, isActive ? self.colors.active : self.colors.base, isActive ? 48 : 40));
      m.setZIndex(isActive ? 999 : 1);
    });
  };

  /** Animate zoom one level at a time for a smoother feel. */
  StoreMap.prototype._smoothZoom = function (target, current) {
    var self = this;
    if (current >= target) {
      this.map.setZoom(target);
      return;
    }
    google.maps.event.addListenerOnce(this.map, 'zoom_changed', function () {
      self._smoothZoom(target, current + 1);
    });
    window.setTimeout(function () {
      self.map.setZoom(current + 1);
    }, 80);
  };

  StoreMap.prototype._infoContent = function (store) {
    var parts = [];
    parts.push('<div class="store-locator__iw">');
    parts.push('<h3 class="store-locator__iw-name">' + escapeHtml(store.name) + '</h3>');
    parts.push('<p class="store-locator__iw-address">' + escapeHtml(store.fullAddress) + '</p>');
    if (store.phone) {
      parts.push(
        '<a class="store-locator__iw-phone" href="tel:' +
          escapeHtml(store.phone.replace(/[^0-9+]/g, '')) +
          '">' +
          escapeHtml(store.phone) +
          '</a>'
      );
    }
    if (store.hours && store.hours.length) {
      parts.push('<ul class="store-locator__iw-hours">');
      store.hours.forEach(function (line) {
        parts.push('<li>' + escapeHtml(line) + '</li>');
      });
      parts.push('</ul>');
    }
    parts.push('<div class="store-locator__iw-actions">');
    parts.push(
      '<a class="btn-comic btn-comic--primary btn-comic--small" target="_blank" rel="noopener" href="' +
        directionsUrl(store) +
        '">Get directions</a>'
    );
    if (store.website) {
      parts.push(
        '<a class="btn-comic btn-comic--blue btn-comic--small" target="_blank" rel="noopener" href="' +
          escapeHtml(store.website) +
          '">Visit store</a>'
      );
    }
    parts.push('</div></div>');
    return parts.join('');
  };

  /* ---------------------------------------------------------------
   * StoreList — the server-rendered list of store cards.
   * ------------------------------------------------------------- */
  function StoreList(root, stores) {
    this.root = root;
    this.stores = stores;
    this.items = Array.prototype.slice.call(
      root.querySelectorAll('[data-store-index]')
    );
    this.emptyState = root.parentNode.querySelector('[data-store-empty]');
    this.emptyDefault = this.emptyState ? this.emptyState.textContent.trim() : '';
    this.onSelect = function () {};
    this.activeIndex = -1;

    // index -> button, plus the original DOM order of <li> rows so we can
    // restore it after a proximity sort.
    this.byIndex = {};
    this.naturalRows = [];
    var self = this;
    this.items.forEach(function (item) {
      var index = parseInt(item.getAttribute('data-store-index'), 10);
      self.byIndex[index] = item;
      self.naturalRows.push(item.parentNode);
      item.addEventListener('click', function () {
        self.onSelect(index);
      });
    });
  }

  /** Find (or create) the little "X mi" badge inside a store card. */
  StoreList.prototype._distanceBadge = function (button, create) {
    var el = button.querySelector('.store-locator__item-distance');
    if (!el && create) {
      el = document.createElement('span');
      el.className = 'store-locator__item-distance';
      button.appendChild(el);
    }
    return el;
  };

  StoreList.prototype._clearDistances = function () {
    this.items.forEach(function (button) {
      var el = button.querySelector('.store-locator__item-distance');
      if (el) {
        el.parentNode.removeChild(el);
      }
    });
  };

  /** Set the empty-state text (pass null/empty to restore the default). */
  StoreList.prototype.setEmptyMessage = function (message) {
    if (this.emptyState) {
      this.emptyState.textContent = message || this.emptyDefault;
    }
  };

  /** Show exactly the given indices (any order); toggles the empty state. */
  StoreList.prototype._setVisibleSet = function (indices) {
    this.items.forEach(function (button) {
      var index = parseInt(button.getAttribute('data-store-index'), 10);
      button.parentNode.hidden = indices.indexOf(index) === -1;
    });
    if (this.emptyState) {
      this.emptyState.hidden = indices.length !== 0;
    }
  };

  StoreList.prototype.setActive = function (index, scroll) {
    this.activeIndex = index;
    this.items.forEach(function (item) {
      var isActive = parseInt(item.getAttribute('data-store-index'), 10) === index;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive && scroll) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  /**
   * Text-search view: natural (server) order, no distance badges, only the
   * matching indices visible. Returns the indices for chaining.
   */
  StoreList.prototype.filter = function (indices) {
    var self = this;
    this._clearDistances();
    this.naturalRows.forEach(function (row) {
      self.root.appendChild(row);
    });
    this._setVisibleSet(indices);
    return indices;
  };

  /**
   * Proximity view: reorder rows nearest-first, stamp each with its distance,
   * and show only the ranked set. `ranked` is [{ index, miles }, ...] sorted.
   */
  StoreList.prototype.showRanked = function (ranked) {
    var self = this;
    this._clearDistances();
    var indices = [];
    ranked.forEach(function (entry) {
      var button = self.byIndex[entry.index];
      if (!button) {
        return;
      }
      self._distanceBadge(button, true).textContent = formatMiles(entry.miles);
      self.root.appendChild(button.parentNode); // move row into ranked order
      indices.push(entry.index);
    });
    this._setVisibleSet(indices);
    return indices;
  };

  /* ---------------------------------------------------------------
   * StoreSearch — real-time filtering by name / city / state / zip.
   * ------------------------------------------------------------- */
  function StoreSearch(input, stores) {
    this.input = input;
    this.stores = stores;
    this.onChange = function () {}; // live text filter (as you type)
    this.onSubmit = function () {}; // geocode + nearest (on Enter)
    var self = this;

    this.index = stores.map(function (store) {
      return [store.name, store.city, store.state, store.zip, store.fullAddress]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    });

    if (input) {
      input.addEventListener('input', function () {
        self.onChange(self.match(input.value), input.value);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          self.onSubmit(input.value);
        }
      });
      // The native search-field clear (×) fires a 'search' event.
      input.addEventListener('search', function () {
        if (!input.value) {
          self.onChange(self.match(''), '');
        }
      });
    }
  }

  StoreSearch.prototype.match = function (query) {
    var terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    var index = this.index;
    var matches = [];
    for (var i = 0; i < index.length; i++) {
      var haystack = index[i];
      var ok = terms.every(function (term) {
        return haystack.indexOf(term) !== -1;
      });
      if (ok) {
        matches.push(i);
      }
    }
    return matches;
  };

  /* ---------------------------------------------------------------
   * StoreLocator — wire the pieces together.
   * ------------------------------------------------------------- */
  function StoreLocator(root) {
    this.root = root;
    this.stores = this._readStores();
    if (!this.stores.length) {
      return;
    }

    this.list = new StoreList(root.querySelector('[data-store-list]'), this.stores);
    this.search = new StoreSearch(root.querySelector('[data-store-search]'), this.stores);
    this.statusEl = root.querySelector('[data-store-status]');
    this.allIndices = this.stores.map(function (_, i) {
      return i;
    });
    this.visible = this.allIndices.slice();

    // How many nearest results to show, and an optional soft radius cap.
    this.resultsLimit = parseInt(root.getAttribute('data-results-limit'), 10) || 10;
    this.maxDistance = parseFloat(root.getAttribute('data-max-distance')) || 0;

    var self = this;
    this.apiKey = root.getAttribute('data-api-key');
    var apiKey = this.apiKey;
    var canvas = root.querySelector('[data-store-map]');

    // Typing filters the list by text; pressing Enter geocodes + ranks by
    // distance. Both work list-only even if the map is still loading / missing.
    this.search.onChange = function (indices, query) {
      self._applyTextFilter(indices, query);
    };
    this.search.onSubmit = function (query) {
      self._locate(query);
    };
    this.list.onSelect = function (index) {
      self._select(index);
    };

    if (!apiKey) {
      this._mapError('Add a Google Maps API key in the theme editor to enable the map.');
      return;
    }

    GoogleMapsLoader.load(apiKey)
      .then(function () {
        self.map = new StoreMap(canvas, self.stores, {
          pinColor: root.getAttribute('data-pin-color') || '#ed1e24',
          pinActiveColor: root.getAttribute('data-pin-active-color') || '#007bc1',
        });
        self.map.onMarkerSelect = function (index) {
          self._select(index, false);
        };
        self.map.fitTo(self.visible);
        root.classList.add('is-map-ready');
      })
      .catch(function () {
        self._mapError('The map could not be loaded. Please try again later.');
      });
  }

  StoreLocator.prototype._readStores = function () {
    var node = this.root.querySelector('[data-store-data]');
    if (!node) {
      return [];
    }
    try {
      var data = JSON.parse(node.textContent);
      return data.map(function (s) {
        var hours = s.hours;
        if (typeof hours === 'string') {
          hours = hours
            .split(/\r?\n/)
            .map(function (line) {
              return line.trim();
            })
            .filter(Boolean);
        }
        return {
          name: s.name,
          fullAddress: s.fullAddress,
          city: s.city,
          state: s.state,
          zip: s.zip,
          phone: s.phone,
          website: s.website,
          hours: hours || [],
          lat: parseFloat(s.lat),
          lng: parseFloat(s.lng),
        };
      });
    } catch (e) {
      return [];
    }
  };

  /* --- Text filter (live, as the user types) --------------------- */
  StoreLocator.prototype._applyTextFilter = function (indices, query) {
    var q = (query || '').trim();

    // Typed something, but no store *name* matches it. Rather than a blank
    // "no results", guide the user to press Enter for a nearest search and
    // keep every pin on the map for context.
    if (q && !indices.length) {
      this.list.setEmptyMessage(
        'No store names match “' + q + '”. Press Enter to find the stores nearest to it.'
      );
      this.visible = this.list.filter([]);
      this._setStatus('');
      if (this.map) {
        this.map.clearSearchMarker();
        this.map.setVisible(this.allIndices);
      }
      return;
    }

    this.list.setEmptyMessage(null);
    this.visible = this.list.filter(indices);
    this._setStatus('');
    if (this.map) {
      this.map.clearSearchMarker();
      this.map.setVisible(indices);
      if (indices.length) {
        this.map.fitTo(indices);
      }
    }
  };

  /* --- Proximity search (on Enter): geocode the query, rank stores. */
  StoreLocator.prototype._locate = function (query) {
    var q = (query || '').trim();
    if (!q) {
      this._applyTextFilter(this.allIndices.slice());
      return;
    }
    var self = this;
    this.root.classList.add('is-searching');
    this._ensureGeocoder()
      .then(function (geocoder) {
        geocoder.geocode(
          { address: q, componentRestrictions: { country: 'US' } },
          function (results, status) {
            self.root.classList.remove('is-searching');
            if (status === 'OK' && results && results[0]) {
              self._showNearest(results[0]);
            } else {
              self._setStatus(
                'We couldn’t find “' + q + '”. Try a ZIP code or city.',
                true
              );
            }
          }
        );
      })
      .catch(function () {
        // No API key / map unavailable: fall back to plain text matching.
        self.root.classList.remove('is-searching');
        self._applyTextFilter(self.search.match(q));
      });
  };

  /** Resolve a Geocoder, loading the Maps API first if needed. */
  StoreLocator.prototype._ensureGeocoder = function () {
    var self = this;
    if (this._geocoder) {
      return Promise.resolve(this._geocoder);
    }
    if (!this.apiKey) {
      return Promise.reject(new Error('No Google Maps API key'));
    }
    return GoogleMapsLoader.load(this.apiKey).then(function () {
      self._geocoder = new window.google.maps.Geocoder();
      return self._geocoder;
    });
  };

  /** Rank stores (that have coordinates) by distance from a lat/lng. */
  StoreLocator.prototype._rankByDistance = function (latLng) {
    var ranked = [];
    this.stores.forEach(function (s, i) {
      if (isNaN(s.lat) || isNaN(s.lng)) {
        return;
      }
      ranked.push({
        index: i,
        miles: haversineMiles(latLng.lat, latLng.lng, s.lat, s.lng),
      });
    });
    ranked.sort(function (a, b) {
      return a.miles - b.miles;
    });
    return ranked;
  };

  /** Show the nearest N stores to a geocoded result, in list and on map. */
  StoreLocator.prototype._showNearest = function (result) {
    var loc = result.geometry.location;
    var latLng = { lat: loc.lat(), lng: loc.lng() };
    var ranked = this._rankByDistance(latLng);

    if (!ranked.length) {
      this._setStatus('No store locations have map coordinates yet.', true);
      return;
    }

    // Nearest N, then a soft radius cap that always keeps at least the
    // closest store so the list is never empty for a valid location.
    var limited = ranked.slice(0, this.resultsLimit);
    if (this.maxDistance > 0) {
      var self = this;
      var within = limited.filter(function (r) {
        return r.miles <= self.maxDistance;
      });
      limited = within.length ? within : [ranked[0]];
    }

    this.visible = this.list.showRanked(limited);
    if (this.map) {
      this.map.setVisible(this.visible);
      this.map.setSearchMarker(latLng);
      this.map.fitTo(this.visible, latLng);
    }
    this._setStatus('Nearest stores to ' + result.formatted_address);
  };

  /** Small status line above the list (results summary or error). */
  StoreLocator.prototype._setStatus = function (message, isError) {
    if (!this.statusEl) {
      return;
    }
    this.statusEl.textContent = message || '';
    this.statusEl.hidden = !message;
    this.statusEl.classList.toggle('is-error', !!isError);
  };

  StoreLocator.prototype._select = function (index, scrollList) {
    this.list.setActive(index, scrollList !== false);
    if (this.map) {
      this.map.focus(index);
    }
  };

  StoreLocator.prototype._mapError = function (message) {
    this.root.classList.add('is-map-error');
    var canvas = this.root.querySelector('[data-store-map]');
    if (canvas) {
      canvas.innerHTML = '<p class="store-locator__map-error">' + escapeHtml(message) + '</p>';
    }
  };

  /* ---------------------------------------------------------------
   * Helpers.
   * ------------------------------------------------------------- */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function directionsUrl(store) {
    var q =
      !isNaN(store.lat) && !isNaN(store.lng)
        ? store.lat + ',' + store.lng
        : store.fullAddress;
    return (
      'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(q)
    );
  }

  /* ---------------------------------------------------------------
   * Boot every locator on the page.
   * ------------------------------------------------------------- */
  function init() {
    var roots = document.querySelectorAll('[data-store-locator]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__storeLocator) {
        return;
      }
      root.__storeLocator = new StoreLocator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a merchant selects/edits the section in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
